<template>
  <div class="subsidiary-management">
    <div class="container">
      <div class="header">
        <h1>Subsidiary Management</h1>
        <p>Manage and configure subsidiary information</p>
      </div>

      <!-- Add Tab Navigation -->
      <div class="tab-navigation">
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'manage' }"
          @click="activeTab = 'manage'"
        >
          <i class="fa-solid fa-list"></i>
          Manage Subsidiaries
        </button>
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'types' }"
          @click="activeTab = 'types'"
        >
          <i class="fa-solid fa-tags"></i>
          Cost & Expense Types
        </button>
      </div>

      <!-- Manage Subsidiaries Tab -->
      <div v-if="activeTab === 'manage'" class="tab-content">
        <div class="filter-description">
          <p>
            <i class="fa-solid fa-circle-info"></i>
            Select a subsidiary to view its details and manage branches.
          </p>
        </div>

        <div class="records-container">
          <div class="card-header">
            <div class="header-icon">
              <i class="fa-solid fa-building"></i>
            </div>
            <h2>Subsidiaries</h2>
          </div>

          <div class="card-actions">
            <button class="add-btn" @click="openAddModal">
              <i class="fas fa-plus"></i> Add Subsidiary
            </button>
          </div>

          <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="sortable" @click="toggleSort('id')">
                    ID
                    <span class="sort-icon">
                      {{ sortField === 'id' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}
                    </span>
                  </th>
                  <th class="sortable" @click="toggleSort('name')">
                    Name
                    <span class="sort-icon">
                      {{ sortField === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}
                    </span>
                  </th>
                  <th class="sortable" @click="toggleSort('reporting_basis')">
                    Reporting Basis
                    <span class="sort-icon">
                      {{ sortField === 'reporting_basis' ? (sortOrder === 'asc' ? '↑' : '↓') : '' }}
                    </span>
                  </th>
                  <th>Created By</th>
                  <th>Updated By</th>
                  <th v-if="!isMobile" class="edit-header">Edit</th>
                  <th v-if="!isMobile" class="delete-header">Delete</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="subsidiary in sortedSubsidiaries" :key="subsidiary.id" @click="openMobileEdit(subsidiary)">
                  <td>{{ subsidiary.id }}</td>
                  <td>{{ subsidiary.name }}</td>
                  <td>{{ subsidiary.reporting_basis }}</td>
                  <td>{{ subsidiary.created_by }}</td>
                  <td>{{ subsidiary.updated_by }}</td>
                  <td v-if="!isMobile" class="edit-action">
                    <button class="edit-btn" @click.stop="openEditModal(subsidiary)" title="Edit">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                  </td>
                  <td v-if="!isMobile" class="delete-action">
                    <button class="cancel-btn" @click.stop="confirmDelete(subsidiary)" title="Delete">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Cost & Expense Types Tab -->
      <div v-if="activeTab === 'types'" class="tab-content">
        <div class="filter-description">
          <p>
            <i class="fa-solid fa-circle-info"></i>
            Select a subsidiary to manage its cost and expense types.
          </p>
        </div>

        <div class="records-container">
          <div class="card-header">
            <div class="header-icon">
              <i class="fa-solid fa-tags"></i>
            </div>
            <h2>Cost & Expense Types</h2>
          </div>

          <div class="filters">
            <div class="filter-group">
              <div class="subsidiary-selector">
                <div class="selected-subsidiary" @click="toggleSubsidiaryDropdown">
                  <span>{{ selectedSubsidiaryName }}</span>
                  <i class="fa-solid fa-chevron-down" :class="{ 'rotate': showSubsidiaryDropdown }"></i>
                </div>
                <div class="subsidiary-dropdown" v-if="showSubsidiaryDropdown">
                  <div class="search-box">
                    <input 
                      type="text" 
                      v-model="subsidiarySearch" 
                      placeholder="Search subsidiaries..."
                      @click.stop
                    >
                  </div>
                  <div 
                    v-for="sub in filteredSubsidiaries" 
                    :key="sub.id" 
                    class="subsidiary-option"
                    :class="{ 'active': selectedSubsidiary === sub.id }"
                    @click="selectSubsidiary(sub)"
                  >
                    {{ sub.name }}
                  </div>
                </div>
              </div>
            </div>
            <button class="add-btn" @click="openAddTypeDialog">
              <i class="fas fa-plus"></i> Add New Type
            </button>
          </div>

          <div class="card-content">
            <div v-if="isMobile">
              <div v-if="!selectedSubsidiary" class="no-data-message">
                <i class="fa-solid fa-building"></i>
                <p>Please select a subsidiary to view its cost and expense types</p>
              </div>
              <div v-else v-for="type in filteredExpenseTypes" :key="type.id" class="expense-mobile-card">
                <div class="mobile-row">
                  <span class="mobile-label">Type:</span>
                  <span class="mobile-value">{{ type.type }}</span>
                </div>
                <div class="mobile-row">
                  <span class="mobile-label">Category:</span>
                  <el-tag :type="type.category === 'stock' ? 'primary' : type.category === 'cost' ? 'success' : 'warning'">
                    {{ type.category }}
                  </el-tag>
                </div>
                <div class="mobile-row action-row">
                  <button 
                    class="btn-primary btn-sm" 
                    @click="editType(type)"
                    :disabled="loadingExpenseTypes"
                  >
                    Edit
                  </button>
                  <button 
                    class="btn-danger btn-sm" 
                    @click="confirmDeleteType(type)"
                    :disabled="loadingExpenseTypes"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
            <table v-else class="data-table">
              <thead>
                <tr>
                  <th class="sortable" @click="toggleTypeSort('type')">
                    Type
                    <span class="sort-icon">
                      {{ typeSortField === 'type' ? (typeSortOrder === 'asc' ? '↑' : '↓') : '' }}
                    </span>
                  </th>
                  <th class="sortable" @click="toggleTypeSort('category')">
                    Category
                    <span class="sort-icon">
                      {{ typeSortField === 'category' ? (typeSortOrder === 'asc' ? '↑' : '↓') : '' }}
                    </span>
                  </th>
                  <th class="actions-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!selectedSubsidiary">
                  <td colspan="3" class="no-data-message">
                    <i class="fa-solid fa-building"></i>
                    <p>Please select a subsidiary to view its cost and expense types</p>
                  </td>
                </tr>
                <tr v-else v-for="type in sortedExpenseTypes" :key="type.id">
                  <td>{{ type.type }}</td>
                  <td>
                    <el-tag :type="type.category === 'stock' ? 'primary' : type.category === 'cost' ? 'success' : 'warning'">
                      {{ type.category }}
                    </el-tag>
                  </td>
                  <td class="actions">
                    <button 
                      class="edit-btn" 
                      @click="editType(type)"
                      :disabled="loadingExpenseTypes"
                      title="Edit"
                    >
                      <i class="fa-solid fa-edit"></i>
                    </button>
                    <button 
                      class="cancel-btn" 
                      @click="confirmDeleteType(type)"
                      :disabled="loadingExpenseTypes"
                      title="Delete"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div class="modal" v-if="showModal" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ isEditing ? 'Edit Subsidiary' : 'Add Subsidiary' }}</h2>
          <button class="btn-icon" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="handleSubmit" class="modal-body">
          <div class="form-group">
            <label for="name">Subsidiary Name</label>
            <input
              type="text"
              id="name"
              v-model="formData.name"
              required
              placeholder="Enter subsidiary name"
            />
          </div>
          <div class="form-group">
            <label for="reporting_basis">Reporting Basis</label>
            <el-select 
              v-model="formData.reporting_basis" 
              placeholder="Select reporting basis"
              style="width: 100%"
            >
              <el-option label="Daily" value="daily" />
              <el-option label="Weekly" value="weekly" />
            </el-select>
          </div>

          <!-- Add Branches Section -->
          <div class="branches-section">
            <div class="section-header">
              <h3>Branches</h3>
              <button type="button" class="btn-secondary btn-sm" @click="addBranch">
                <i class="fas fa-plus"></i> Add Branch
              </button>
            </div>
            
            <div class="branches-grid">
              <template v-for="(column, columnIndex) in branchColumns" :key="columnIndex">
                <div class="branch-column">
                  <div v-for="(branch, index) in column" :key="columnIndex * 5 + index" class="branch-item">
                    <div class="branch-input-group">
                      <input
                        type="text"
                        :id="'branch-name-' + (columnIndex * 5 + index)"
                        v-model="branch.branch_name"
                        required
                        placeholder="Enter branch name"
                      />
                      <button 
                        type="button" 
                        class="btn-icon delete" 
                        @click="removeBranch(columnIndex * 5 + index)"
                        title="Remove branch"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary">
              {{ isEditing ? 'Update' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal" v-if="showDeleteModal" @click.self="closeDeleteModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Confirm Delete</h2>
          <button class="btn-icon" @click="closeDeleteModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete the subsidiary "{{ subsidiaryToDelete?.name }}"?</p>
          <p class="warning">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" @click="closeDeleteModal">Cancel</button>
          <button type="button" class="btn-danger" @click="handleDelete">Delete</button>
        </div>
      </div>
    </div>

    <!-- Mobile Edit Modal -->
    <div v-if="isMobile && mobileEditRow" class="modal" @click.self="closeMobileEdit">
      <div class="modal-content" style="max-width: 350px;">
        <div class="modal-header">
          <h2>Edit Subsidiary</h2>
          <button class="btn-icon" @click="closeMobileEdit">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="saveMobileEdit" class="modal-body">
          <div class="form-group" style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 0.9rem; color: #64748b;">ID:</span>
            <span style="font-size: 1rem; color: #64748b; font-weight: 600;">{{ mobileEditRow.id }}</span>
          </div>
          <div class="form-group">
            <label for="mobile-name">Subsidiary Name</label>
            <input
              type="text"
              id="mobile-name"
              v-model="mobileEditName"
              required
              placeholder="Enter subsidiary name"
            />
          </div>
          <div class="form-group">
            <label for="mobile-basis">Reporting Basis</label>
            <select id="mobile-basis" v-model="mobileEditBasis" required>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          <div class="modal-footer" style="flex-direction: column; gap: 8px; align-items: stretch; padding-top: 16px;">
            <button type="button" class="btn-danger" @click.prevent="confirmDelete(mobileEditRow)">Delete</button>
            <div class="modal-footer-row">
              <button type="button" class="btn-cancel" @click="closeMobileEdit">Cancel</button>
              <button type="submit" class="btn-primary">Save</button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Add Toast Container -->
    <div class="toast-container" v-if="showToast">
      <div :class="['toast', toastType]">
        <span class="icon" v-if="toastType === 'success'">✓</span>
        {{ toastMessage }}
      </div>
    </div>

    <!-- Add/Edit Type Dialog -->
    <el-dialog
      :title="editingType ? 'Edit Type' : 'Add New Type'"
      v-model="showAddTypeDialog"
      width="500px"
    >
      <el-form :model="newType" label-width="100px">
        <template v-if="!editingType">
          <el-form-item label="Selection">
            <el-radio-group v-model="typeSelection" class="type-selection-group">
              <el-radio :value="'existing'">Use Existing Type</el-radio>
              <el-radio :value="'new'">Create New Type</el-radio>
            </el-radio-group>
          </el-form-item>

          <template v-if="typeSelection === 'existing'">
            <el-form-item label="Type">
              <el-select 
                v-model="newType.selectedTypes" 
                placeholder="Select existing types"
                :disabled="loadingExpenseTypes"
                style="width: 100%"
                filterable
                multiple
                collapse-tags
                collapse-tags-tooltip
              >
                <el-option
                  v-for="type in availableTypes"
                  :key="type.id"
                  :label="type.type"
                  :value="type.id"
                >
                  <span>{{ type.type }}</span>
                  <span style="float: right; color: #8492a6; font-size: 13px">
                    {{ type.category }}
                  </span>
                </el-option>
                <template v-if="availableTypes.length === 0">
                  <el-option disabled :value="''">
                    <span style="color: #909399">No available types</span>
                  </el-option>
                </template>
              </el-select>
            </el-form-item>
          </template>

          <template v-if="typeSelection === 'new'">
            <el-form-item label="Type Name">
              <el-input 
                v-model="newType.type" 
                placeholder="Enter type name"
                :disabled="loadingExpenseTypes"
              />
            </el-form-item>
            <el-form-item label="Category">
              <el-select 
                v-model="newType.category" 
                placeholder="Select category"
                :disabled="loadingExpenseTypes"
                style="width: 100%"
              >
                <el-option label="Cost" value="cost" />
                <el-option label="Expense" value="expense" />
                <el-option label="Stock" value="stock" />
              </el-select>
            </el-form-item>
          </template>
        </template>

        <template v-else>
          <el-form-item label="Type Name">
            <el-input 
              v-model="newType.type" 
              placeholder="Enter type name"
              :disabled="loadingExpenseTypes"
            />
          </el-form-item>
          <el-form-item label="Category">
            <el-select 
              v-model="newType.category" 
              placeholder="Select category"
              :disabled="loadingExpenseTypes"
              style="width: 100%"
            >
              <el-option label="Cost" value="cost" />
              <el-option label="Expense" value="expense" />
              <el-option label="Stock" value="stock" />
            </el-select>
          </el-form-item>
        </template>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="closeTypeDialog">Cancel</el-button>
          <el-button 
            type="primary" 
            @click="saveType"
            :disabled="!isFormValid || loadingExpenseTypes"
          >
            {{ getSubmitButtonText }}
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- Delete Type Confirmation Dialog -->
    <el-dialog
      title="Confirm Delete"
      v-model="showDeleteDialog"
      width="400px"
    >
      <p>Are you sure you want to delete this type?</p>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="closeDeleteDialog">Cancel</el-button>
          <el-button 
            type="danger" 
            @click="deleteType"
            :disabled="loadingExpenseTypes"
          >
            Delete
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'

// State
const subsidiaries = ref([])
const showModal = ref(false)
const showDeleteModal = ref(false)
const isEditing = ref(false)
const subsidiaryToDelete = ref(null)
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('')
const sortField = ref('id')
const sortOrder = ref('asc')
const typeSortField = ref('type')
const typeSortOrder = ref('asc')
const isMobile = ref(window.innerWidth <= 768)
const mobileEditRow = ref(null)
const mobileEditName = ref('')
const mobileEditBasis = ref('daily')
const activeTab = ref('manage')

// Add new refs for expense types and subsidiary filtering
const expenseTypes = ref([])
const loadingExpenseTypes = ref(false)
const showAddTypeDialog = ref(false)
const showDeleteDialog = ref(false)
const editingType = ref(null)
const typeToDelete = ref(null)
const selectedSubsidiary = ref('')
const showSubsidiaryDropdown = ref(false)
const subsidiarySearch = ref('')

// Add new refs for type selection
const typeSelection = ref('existing')
const existingTypes = ref([])
const loadingExistingTypes = ref(false)

const formData = ref({
  name: '',
  reporting_basis: 'daily',
  branches: []
})

// Update newType ref to include selectedTypes
const newType = ref({
  type: '',
  category: 'cost',
  selectedTypes: []
})

// Update filteredExpenseTypes computed property
const filteredExpenseTypes = computed(() => {
  return expenseTypes.value
})

// Update fetchExpenseTypes to fetch only assigned types
const fetchExpenseTypes = async (subsidiaryId = null) => {
  loadingExpenseTypes.value = true
  try {
    const userData = JSON.parse(sessionStorage.getItem('user'))
    const targetSubsidiaryId = subsidiaryId || userData.subsidiary_id

    const response = await api.get(`/api/cost-submission/expense-types/${targetSubsidiaryId}?type=assigned`)
    expenseTypes.value = response.data
  } catch (error) {
    ElMessage.error('Failed to fetch expense types')
  } finally {
    loadingExpenseTypes.value = false
  }
}

// Update fetchExistingTypes to fetch all available types
const fetchExistingTypes = async () => {
  loadingExistingTypes.value = true
  try {
    // Fetch all expense types that are not yet assigned to this subsidiary
    const response = await api.get(`/api/cost-submission/expense-types/${selectedSubsidiary.value}?type=available`)
    existingTypes.value = response.data
  } catch (error) {
    if (error.response) {
    }
    ElMessage.error('Failed to fetch available types')
    throw error
  } finally {
    loadingExistingTypes.value = false
  }
}

// Update saveType function
const saveType = async () => {
  loadingExpenseTypes.value = true
  try {
    if (editingType.value) {
      // Update existing type
      await api.put(`/api/cost-submission/expense-types/${editingType.value.id}`, {
        type: newType.value.type,
        category: newType.value.category
      })
      ElMessage.success('Type updated successfully')
    } else if (typeSelection.value === 'existing') {
      // Assign multiple existing types to subsidiary
      const assignments = newType.value.selectedTypes.map(typeId => 
        api.post('/api/cost-submission/expense-types/assign', {
          expense_type_id: typeId,
          subsidiary_id: selectedSubsidiary.value
        })
      )
      await Promise.all(assignments)
      ElMessage.success('Types assigned successfully')
    } else {
      // Create new type
      const response = await api.post('/api/cost-submission/expense-types', {
        type: newType.value.type,
        category: newType.value.category
      })

      // Create association for new type
      await api.post('/api/cost-submission/expense-types/assign', {
        expense_type_id: response.data.id,
        subsidiary_id: selectedSubsidiary.value
      })
      ElMessage.success('Type added successfully')
    }
    closeTypeDialog()
    await fetchExpenseTypes(selectedSubsidiary.value)
  } catch (error) {
    if (error.response?.data?.message) {
      ElMessage.error(error.response.data.message)
    } else {
      ElMessage.error('Failed to save type')
    }
  } finally {
    loadingExpenseTypes.value = false
  }
}

// Update openAddTypeDialog function
const openAddTypeDialog = async () => {
  if (!selectedSubsidiary.value) {
    ElMessage.warning('Please select a subsidiary first')
    return
  }
  editingType.value = null
  typeSelection.value = 'existing'
  newType.value = {
    type: '',
    category: 'cost',
    selectedTypes: []
  }
  
  // Fetch existing types before showing dialog
  try {
    await fetchExistingTypes()
    showAddTypeDialog.value = true
  } catch (error) {
    ElMessage.error('Failed to load available types')
  }
}

// Computed property for sorted subsidiaries
const sortedSubsidiaries = computed(() => {
  return [...subsidiaries.value].sort((a, b) => {
    const aValue = a[sortField.value]
    const bValue = b[sortField.value]
    
    if (typeof aValue === 'string') {
      return sortOrder.value === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    }
    
    return sortOrder.value === 'asc' 
      ? aValue - bValue
      : bValue - aValue
  })
})

// Update sortedExpenseTypes computed property
const sortedExpenseTypes = computed(() => {
  return [...expenseTypes.value].sort((a, b) => {
    // First sort by category (stock first, then cost, then expense)
    if (a.category !== b.category) {
      if (a.category === 'stock') return -1
      if (b.category === 'stock') return 1
      if (a.category === 'cost') return -1
      if (b.category === 'cost') return 1
      return 0
    }
    
    // Then sort by type name
    const aValue = a[typeSortField.value]
    const bValue = b[typeSortField.value]
    
    if (typeof aValue === 'string') {
      return typeSortOrder.value === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    }
    
    return typeSortOrder.value === 'asc' 
      ? aValue - bValue
      : bValue - aValue
  })
})

// Update the computed property for horizontal scrollable columns
const branchColumns = computed(() => {
  const branches = formData.value.branches
  const rowsPerColumn = 5
  const columnCount = Math.ceil(branches.length / rowsPerColumn)
  const columns = Array.from({ length: columnCount }, () => [])
  for (let i = 0; i < branches.length; i++) {
    const col = Math.floor(i / rowsPerColumn)
    columns[col].push(branches[i])
  }
  return columns
})

// API instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}, error => {
  return Promise.reject(error)
})

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      showNotification('Authentication failed. Please log in again.', 'error')
      // Optionally redirect to login page
      // window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Toast methods
const showNotification = (message, type = 'success') => {
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

// Methods
const fetchSubsidiaries = async () => {
  try {
    const response = await api.get('/api/subsidiary')
    subsidiaries.value = response.data
  } catch (error) {
    showNotification('Failed to fetch subsidiaries', 'error')
  }
}

const openAddModal = () => {
  isEditing.value = false
  formData.value = {
    name: '',
    reporting_basis: 'daily',
    branches: []
  }
  showModal.value = true
}

const openEditModal = async (subsidiary) => {
  isEditing.value = true
  formData.value = { 
    ...subsidiary,
    branches: []
  }
  
  // Fetch branches for this subsidiary
  try {
    const response = await api.get(`/api/finance/subsidiary/${subsidiary.id}/branches`)
    formData.value.branches = response.data
  } catch (error) {
    showNotification('Failed to fetch branches', 'error')
  }
  
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  formData.value = {
    name: '',
    reporting_basis: 'daily',
    branches: []
  }
}

const handleSubmit = async () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user'))
    if (!user || !user.id) {
      showNotification('User information not found', 'error')
      return
    }

    const token = sessionStorage.getItem('token')
    if (!token) {
      showNotification('Authentication token not found', 'error')
      return
    }

    let subsidiaryResponse
    if (isEditing.value) {
      // Update subsidiary
      subsidiaryResponse = await api.put(`/api/subsidiary/${formData.value.id}`, {
        name: formData.value.name,
        reporting_basis: formData.value.reporting_basis,
        created_by: user.id,
        updated_by: user.id
      })

      // Get current branches
      const currentBranchesResponse = await api.get(`/api/finance/subsidiary/${formData.value.id}/branches`)
      const currentBranches = currentBranchesResponse.data

      // Get IDs of branches that should remain
      const remainingBranchIds = formData.value.branches
        .filter(branch => branch.id)
        .map(branch => branch.id)

      // Delete branches that are no longer in the form
      for (const branch of currentBranches) {
        if (!remainingBranchIds.includes(branch.id)) {
          await api.delete(`/api/branches/${branch.id}`)
        }
      }

      // Update or create branches
      for (const branch of formData.value.branches) {
        if (branch.id) {
          // Update existing branch
          await api.put(`/api/branches/${branch.id}`, {
            branch_name: branch.branch_name,
            subsidiary_id: formData.value.id
          })
        } else if (branch.branch_name) {
          // Create new branch
          await api.post('/api/branches', {
            branch_name: branch.branch_name,
            subsidiary_id: formData.value.id
          })
        }
      }
    } else {
      // Create new subsidiary
      subsidiaryResponse = await api.post('/api/subsidiary', {
        name: formData.value.name,
        reporting_basis: formData.value.reporting_basis,
        created_by: user.id,
        updated_by: user.id
      })

      const subsidiaryId = subsidiaryResponse.data.id

      // Create new branches
      for (const branch of formData.value.branches) {
        if (branch.branch_name) {
          await api.post('/api/branches', {
            branch_name: branch.branch_name,
            subsidiary_id: subsidiaryId
          })
        }
      }
    }

    showNotification(isEditing.value ? 'Subsidiary updated successfully' : 'Subsidiary created successfully')
    await fetchSubsidiaries()
    closeModal()
  } catch (error) {
    if (error.response?.data?.error) {
      showNotification(error.response.data.error, 'error')
    } else {
      showNotification(isEditing.value ? 'Failed to update subsidiary' : 'Failed to create subsidiary', 'error')
    }
  }
}

const confirmDelete = (subsidiary) => {
  subsidiaryToDelete.value = subsidiary
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  subsidiaryToDelete.value = null
}

const handleDelete = async () => {
  try {
    await api.delete(`/api/subsidiary/${subsidiaryToDelete.value.id}`)
    showNotification('Subsidiary deleted successfully')
    await fetchSubsidiaries()
    closeDeleteModal()
  } catch (error) {
    if (error.response?.status === 400) {
      const errorMessage = error.response.data.message || error.response.data.error
      if (error.response.data.picUsers) {
        const picUsers = error.response.data.picUsers
        const userList = picUsers.map(user => `${user.name} (${user.branch})`).join('\n')
        showNotification(
          `Cannot delete subsidiary. The following PIC users need to be reassigned first:\n${userList}`,
          'error'
        )
      } else {
        showNotification(errorMessage || 'Failed to delete subsidiary', 'error')
      }
    } else {
      showNotification('Failed to delete subsidiary', 'error')
    }
  }
}

const formatDate = (date) => {
  return new Date(date).toLocaleString()
}

const toggleSort = (field) => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'asc'
  }
}

const toggleTypeSort = (field) => {
  if (typeSortField.value === field) {
    typeSortOrder.value = typeSortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    typeSortField.value = field
    typeSortOrder.value = 'asc'
  }
}

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768
}

const openMobileEdit = (subsidiary) => {
  if (!isMobile.value) return
  mobileEditRow.value = subsidiary
  mobileEditName.value = subsidiary.name
  mobileEditBasis.value = subsidiary.reporting_basis
}

const closeMobileEdit = () => {
  mobileEditRow.value = null
  mobileEditName.value = ''
  mobileEditBasis.value = 'daily'
}

const saveMobileEdit = async () => {
  if (!mobileEditRow.value) return
  try {
    await api.put(`/api/subsidiary/${mobileEditRow.value.id}`, {
      name: mobileEditName.value,
      reporting_basis: mobileEditBasis.value
    })
    showNotification('Subsidiary updated successfully')
    await fetchSubsidiaries()
    closeMobileEdit()
  } catch (error) {
    showNotification('Failed to update subsidiary', 'error')
  }
}

const addBranch = () => {
  formData.value.branches.push({
    branch_name: '',
    subsidiary_id: null
  })
}

const removeBranch = (index) => {
  formData.value.branches.splice(index, 1)
}

const closeTypeDialog = () => {
  showAddTypeDialog.value = false
  editingType.value = null
  newType.value = {
    type: '',
    category: 'cost',
    selectedTypes: []
  }
}

const editType = (type) => {
  editingType.value = type
  newType.value = { ...type }
  showAddTypeDialog.value = true
}

const confirmDeleteType = (type) => {
  typeToDelete.value = type
  showDeleteDialog.value = true
}

const closeDeleteDialog = () => {
  showDeleteDialog.value = false
  typeToDelete.value = null
}

const deleteType = async () => {
  if (!typeToDelete.value) return

  loadingExpenseTypes.value = true
  try {
    // Delete the type from the subsidiary (unassign it)
    await api.delete(`/api/cost-submission/expense-types/${typeToDelete.value.id}?subsidiary_id=${selectedSubsidiary.value}`)
    ElMessage.success('Type removed from subsidiary successfully')
    closeDeleteDialog()
    await fetchExpenseTypes(selectedSubsidiary.value)
  } catch (error) {
    if (error.response?.data?.message) {
      ElMessage.error(error.response.data.message)
    } else {
      ElMessage.error('Failed to delete type')
    }
  } finally {
    loadingExpenseTypes.value = false
  }
}

const isFormValid = computed(() => {
  if (editingType.value) {
    return newType.value.type && newType.value.category
  }
  if (typeSelection.value === 'existing') {
    return Array.isArray(newType.value.selectedTypes) && 
           newType.value.selectedTypes.length > 0
  }
  return newType.value.type && newType.value.category
})

const selectedSubsidiaryName = computed(() => {
  const selected = subsidiaries.value.find(sub => sub.id === selectedSubsidiary.value)
  return selected ? selected.name : 'Select Subsidiary'
})

const filteredSubsidiaries = computed(() => {
  const sortedSubsidiaries = [...subsidiaries.value].sort((a, b) => a.id - b.id)
  if (!subsidiarySearch.value) return sortedSubsidiaries
  const query = subsidiarySearch.value.toLowerCase()
  return sortedSubsidiaries.filter(sub => 
    sub.name.toLowerCase().includes(query)
  )
})

const toggleSubsidiaryDropdown = (event) => {
  event.stopPropagation()
  showSubsidiaryDropdown.value = !showSubsidiaryDropdown.value
}

const selectSubsidiary = async (subsidiary) => {
  selectedSubsidiary.value = subsidiary.id
  showSubsidiaryDropdown.value = false
  await fetchExpenseTypes(subsidiary.id)
  await fetchExistingTypes()
}

// Add click outside handler
const handleClickOutside = (event) => {
  const subsidiarySelector = document.querySelector('.subsidiary-selector')
  if (subsidiarySelector && !subsidiarySelector.contains(event.target)) {
    showSubsidiaryDropdown.value = false
  }
}

// Add computed property for available types
const availableTypes = computed(() => {
  if (!selectedSubsidiary.value) {
    return []
  }

  // Get IDs of types already assigned to the selected subsidiary
  const assignedTypeIds = expenseTypes.value.map(type => type.id)

  // Filter out types that are already assigned to this subsidiary
  return existingTypes.value.filter(type => !assignedTypeIds.includes(type.id))
})

// Add computed property for submit button text
const getSubmitButtonText = computed(() => {
  if (editingType.value) return 'Save Changes'
  if (typeSelection.value === 'existing') return 'Assign Types'
  return 'Add Type'
})

// Initialize
onMounted(async () => {
  await fetchSubsidiaries()
  await fetchExpenseTypes()
  window.addEventListener('resize', handleResize)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.subsidiary-management {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
  position: relative;
  z-index: 1;
}

.container {
  width: 100%;
  margin: 0 auto;
}

.header {
  margin-bottom: 32px;
}

.header h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
  margin-bottom: 8px;
}

.header p {
  font-size: 0.875rem;
  color: #64748b;
}

.filter-description {
  margin-bottom: 24px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.filter-description p {
  font-size: 0.813rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.filter-description i {
  color: #0ea5e9;
  font-size: 0.875rem;
}

.records-container {
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e0f2fe;
  color: #0ea5e9;
}

.header-icon i {
  font-size: 1.5rem;
}

.card-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.tab-navigation {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 16px;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  background: none;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 8px;
}

.tab-button:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.tab-button.active {
  background: #0ea5e9;
  color: white;
}

.tab-button i {
  font-size: 1rem;
}

.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-right: 24px;
}

.sortable:hover {
  background-color: #f1f5f9;
}

.sort-icon {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

/* Table Styles */
.table-container {
  overflow-x: auto;
  margin-top: 16px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.data-table th,
.data-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.data-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.data-table tbody tr:hover {
  background: #f8fafc;
  transition: background 0.2s;
}

.data-table td.actions {
  text-align: center;
  white-space: nowrap;
}

.actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.edit-btn, .save-btn, .cancel-btn {
  padding: 6px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.edit-btn {
  color: #0ea5e9;
}

.save-btn {
  color: #16a34a;
}

.cancel-btn {
  color: #ef4444;
}

.edit-btn:hover {
  background: #bae6fd;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.save-btn:hover {
  background: #bbf7d0;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.cancel-btn:hover {
  background: #fecaca;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #1e40af;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #e2e8f0;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-danger:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.btn-sm {
  padding: 6px 12px;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .subsidiary-management {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .modal-content {
    min-width: unset;
    width: 95vw;
    margin: 16px;
  }

  .btn-primary,
  .btn-secondary,
  .btn-danger {
    width: 100%;
    justify-content: center;
    padding: 8px 10px;
    font-size: 0.8rem;
    border-radius: 5px;
  }
}

/* Add Toast Styles */
.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
  min-width: 300px;
}

.toast {
  padding: 16px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.toast.success {
  background-color: #dcfce7;
  color: #16a34a;
  border-color: rgba(22, 163, 74, 0.1);
}

.toast.error {
  background-color: #fee2e2;
  color: #dc2626;
  border-color: rgba(220, 38, 38, 0.1);
}

.toast .icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: currentColor;
  color: white;
  font-size: 14px;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .toast-container {
    left: 24px;
    right: 24px;
    transform: none;
  }
}

.id-col {
  min-width: 60px;
  width: 80px;
  max-width: 120px;
}

.modal-footer-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.branches-section {
  margin-top: 24px;
  border-top: 1px solid #e2e8f0;
  padding-top: 16px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #475569;
  margin: 0;
}

.branches-grid {
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(5, auto);
  gap: 12px;
  overflow-x: auto;
  max-width: 100%;
  max-height: 320px;
  padding-bottom: 8px;
  min-height: 0;
}

.branch-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 200px;
  height: fit-content;
}

.branch-item {
  margin: 0;
  padding: 0;
  height: fit-content;
}

.branch-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 0;
}

.branch-input-group input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  min-width: 0;
  margin: 0;
  height: 36px;
}

.data-table th.actions-header {
  text-align: center;
}
.data-table th.edit-header,
.data-table th.delete-header {
  text-align: center;
  width: 60px;
  min-width: 60px;
  max-width: 70px;
}
.data-table td.edit-action,
.data-table td.delete-action {
  text-align: center;
  vertical-align: middle;
  width: 60px;
  min-width: 60px;
  max-width: 70px;
}

/* Add new styles for expense types */
.expense-mobile-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 0.875rem;
}

.btn-danger {
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger:hover {
  background: #dc2626;
}

.btn-danger:disabled {
  background: #fecaca;
  cursor: not-allowed;
}

:deep(.el-tag) {
  text-transform: capitalize;
}

:deep(.el-tag--success) {
  background-color: #dcfce7;
  border-color: #bbf7d0;
  color: #16a34a;
}

:deep(.el-tag--warning) {
  background-color: #fef3c7;
  border-color: #fde68a;
  color: #d97706;
}

.tab-navigation {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.tab-button {
  padding: 12px 24px;
  border-radius: 8px;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-button:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.tab-button.active {
  background: #0ea5e9;
  color: white;
}

.tab-button i {
  font-size: 1rem;
}

.tab-content {
  margin-top: 24px;
}

/* Add new styles for subsidiary filter */
.filter-section {
  margin-right: 16px;
}

.subsidiary-filter {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #475569;
  background-color: white;
  min-width: 200px;
  cursor: pointer;
  transition: all 0.2s;
}

.subsidiary-filter:hover {
  border-color: #94a3b8;
}

.subsidiary-filter:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 24px;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #1e40af;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-btn:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

@media (max-width: 768px) {
  .card-actions {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
  }
  
  .add-btn {
    width: 100%;
    justify-content: center;
    padding: 8px 10px;
    font-size: 0.8rem;
    border-radius: 5px;
  }
}

.filters {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 24px;
}

.filter-group {
  flex: 1;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  transition: all 0.2s;
  background-color: white;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-group input:hover,
.form-group select:hover {
  border-color: #94a3b8;
}

.form-group input::placeholder {
  color: #94a3b8;
}

.subsidiary-selector {
  position: relative;
  min-width: 200px;
  z-index: 1000;
}

.selected-subsidiary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  cursor: pointer;
  transition: all 0.2s;
}

.selected-subsidiary:hover {
  border-color: #cbd5e1;
}

.selected-subsidiary span {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
}

.selected-subsidiary i {
  font-size: 0.875rem;
  color: #64748b;
  transition: transform 0.2s;
}

.selected-subsidiary i.rotate {
  transform: rotate(180deg);
}

.subsidiary-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 1001;
  max-height: 300px;
  overflow-y: auto;
}

.search-box {
  padding: 8px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.search-box input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.subsidiary-option {
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: #1e293b;
}

.subsidiary-option:hover {
  background: #f1f5f9;
}

.subsidiary-option.active {
  background: #f1f5f9;
  color: #0ea5e9;
  font-weight: 500;
}

/* Update table styles to handle no data message */
.data-table td.no-data-message {
  padding: 48px 24px;
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
  width: 100%;
  display: table-cell;
  margin: 24px 0;
}

/* Add styles to ensure the message spans all columns */
.data-table tr:has(td.no-data-message) {
  display: table-row;
  height: 200px; /* Add fixed height to ensure proper spacing */
}

.data-table tr:has(td.no-data-message) td {
  padding: 0;
  border: none;
  vertical-align: middle; /* Center vertically */
}

.data-table tr:has(td.no-data-message) td:first-child {
  padding-left: 0;
}

.data-table tr:has(td.no-data-message) td:last-child {
  padding-right: 0;
}

/* Add styles for no data message */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
  min-height: 120px; /* Add minimum height */
}

.no-data-message i {
  font-size: 2rem;
  margin-bottom: 16px;
  color: #94a3b8;
}

.no-data-message p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

/* Add new styles for type selection */
.type-selection-group {
  display: flex;
  gap: 24px;
  margin-top: 8px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #1e293b;
}

.multi-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  min-height: 120px;
  background: white;
}

.multi-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.multi-select option {
  padding: 8px;
  margin: 4px 0;
  border-radius: 4px;
}

.multi-select option:hover {
  background: #f1f5f9;
}

/* Add new styles for dialogs */
:deep(.el-dialog) {
  border-radius: 12px;
  overflow: hidden;
}

:deep(.el-dialog__header) {
  margin: 0;
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}

:deep(.el-dialog__title) {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

:deep(.el-dialog__body) {
  padding: 24px;
}

:deep(.el-dialog__footer) {
  padding: 20px;
  border-top: 1px solid #e2e8f0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.type-selection-group {
  display: flex;
  gap: 24px;
}

:deep(.el-radio) {
  margin-right: 0;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #1e293b;
}

:deep(.el-input__wrapper),
:deep(.el-select .el-input__wrapper) {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  box-shadow: none;
}

:deep(.el-input__wrapper:hover),
:deep(.el-select .el-input__wrapper:hover) {
  border-color: #94a3b8;
}

:deep(.el-input__wrapper.is-focus),
:deep(.el-select .el-input__wrapper.is-focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

@media (max-width: 768px) {
  :deep(.el-dialog) {
    width: 95vw !important;
    min-width: unset !important;
    max-width: 100vw !important;
  }
  
  :deep(.el-dialog__body) {
    padding: 12px !important;
  }
  
  :deep(.el-form-item__label) {
    font-size: 0.875rem;
  }
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  margin: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 1;
}

.modal-footer .btn-primary {
  background: #409eff;
  border-color: #409eff;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.modal-footer .btn-primary:hover {
  background: #66b1ff;
  border-color: #66b1ff;
}

.modal-footer .btn-secondary {
  background: white;
  border: 1px solid #dcdfe6;
  color: #606266;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.modal-footer .btn-secondary:hover {
  color: #409eff;
  border-color: #c6e2ff;
  background-color: #ecf5ff;
}

@media (max-width: 768px) {
  .modal-content {
    margin: 16px;
    max-height: 95vh;
  }
}
</style> 